import { HTMLAttributes, useEffect, useRef } from 'react'
import gsap from 'gsap'

import Reveal from '@/components/base/reveal'
import Typography from '@/components/base/typography'
import { useLenisStore } from '@/contexts/lenis'
import { useStore } from '@/contexts/store'
import { useLayoutStore } from '@/store/layout'
import { useRouterStore } from '@/store/router'

import { footerRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export type FooterProps = HTMLAttributes<HTMLElement> & {
  pageProps: WPPageData
}

const Footer = ({ pageProps, className, ...props }: FooterProps) => {
  const { strings } = useStore()
  const { footer } = strings || {}
  const { layout } = pageProps?.acf || {}

  const footerDark = useLayoutStore((state) => state.footerDark)
  const footerVisible = useLayoutStore((state) => state.footerVisible)

  const scrollLine = useRef<HTMLDivElement>(null!)

  useEffect(() => {
    const unsubscribe = useLenisStore.subscribe(
      (state) => state.progress,
      (progress) => gsap.set(scrollLine.current, { scaleX: progress })
    )

    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    const unsubscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      (_, prevRoute) =>
        gsap.to(scrollLine.current, {
          scaleX: 0,
          duration: prevRoute !== undefined ? 0 : 0.5,
          ease: 'power2.inOut'
        }),
      { fireImmediately: true }
    )

    return () => {
      unsubscribe()
    }
  }, [])

  return (
    <Reveal animation={footerRevealAnimation}>
      {(revealRef) => (
        <footer
          ref={revealRef}
          className={cn([
            { hidden: !footerVisible },
            'relative',
            'lg:static',
            className
          ])}
          {...props}
        >
          <div
            className={cn(
              'flex justify-center p-margin lg:p-0',
              'lg:fixed lg:bottom-[3.6rem] lg:left-[4rem] lg:z-footer'
            )}
          >
            <div
              className={cn(
                'flex items-center gap-[1.2rem] overflow-hidden',
                'lg:transition-opacity lg:duration-500 lg:ease-in-out-cubic'
              )}
            >
              <Typography
                className={cn(
                  'flex text-[1.28rem] capitalize tracking-[0.0384rem]',
                  footerDark ? 'text-dark/50' : 'text-white/50'
                )}
                data-motion-colophon
              >
                {footer?.colophon.replace('[YEAR]', new Date().getFullYear())}
              </Typography>
            </div>
          </div>

          <div
            className={cn(
              'lg:items-left hidden lg:flex lg:flex-col lg:space-y-[1.2rem]',
              'lg:fixed lg:bottom-[3.6rem] lg:right-[4rem] lg:z-20',
              'lg:transition-opacity lg:duration-500 lg:ease-in-out-cubic'
              // 'lg:opacity-0 lg:[.ui-reveal_&]:opacity-100'
            )}
          >
            <div className="relative h-[1px] w-[20rem]">
              <span
                className={cn(
                  'absolute left-0 top-0 h-full w-full origin-right',
                  'bg-white/25'
                )}
                data-motion-scroll-line
              />
              <div
                ref={scrollLine}
                className={cn(
                  'absolute left-0 top-0 h-full w-full origin-left',
                  'bg-white'
                )}
              />
            </div>

            <div className="flex overflow-hidden">
              <Typography
                className={cn(
                  'flex text-[1.28rem] font-bold capitalize tracking-[0.0384rem]',
                  footerDark ? 'text-black/50' : 'text-white/50'
                )}
                data-motion-scroll-label
              >
                {layout?.footer?.label}
              </Typography>
            </div>
          </div>
        </footer>
      )}
    </Reveal>
  )
}

export default Footer
